import type { AxiosInstance } from 'axios';
import queryString from 'query-string';
import type { UploadCredential } from '../types/index.js';
import type * as ChatApi from './interfaces/chat.js';
import {
  prepareContextData,
  stringifyContextData,
  transformChatInstance,
  transformChatQuery,
} from './transformers/index.js';

export class ChatBaseClient {
  constructor(private authenticatedClient: AxiosInstance) {}

  submitChatQuery = async (data: ChatApi.SubmitChatQuery.Request) => {
    const { metadata } = data.context;
    const preparedContext = prepareContextData(data.context);
    const postData = {
      ...data,
      context: {
        ...(preparedContext as object),
        ...(metadata ? { metadata } : {}),
      },
      displayMode: data.displayMode,
    };
    const res = await this.authenticatedClient.post<ChatApi.SubmitChatQuery.Response>(
      '/chat/submit-query',
      postData
    );
    return {
      identifier: res.data.identifier,
    };
  };

  getInputAttachmentUploadCredentials = async (data: {
    files: Array<{ name: string; mimeType: string; sizeBytes: number }>;
  }): Promise<{ uploads: UploadCredential[] }> => {
    const res = await this.authenticatedClient.post<{ uploads: UploadCredential[] }>(
      '/chat/input-attachment/upload-credentials',
      data
    );
    return res.data;
  };

  extractInputAttachment = async ({
    identifier,
    ...body
  }: ChatApi.ExtractInputAttachment.Request): Promise<ChatApi.ExtractInputAttachment.Response> => {
    const res = await this.authenticatedClient.post<ChatApi.ExtractInputAttachment.Response>(
      `/chat/input-attachment/${identifier}/extract`,
      body
    );
    return res.data;
  };

  getInputAttachmentExtractionStatus = async ({
    identifiers,
  }: ChatApi.GetInputAttachmentExtractionStatus.Request): Promise<ChatApi.GetInputAttachmentExtractionStatus.Response> => {
    const qs = queryString.stringify({ identifiers: identifiers.join(',') });
    const res = await this.authenticatedClient.get<ChatApi.GetInputAttachmentExtractionStatus.Response>(
      `/chat/input-attachment/extraction-status?${qs}`
    );
    return res.data;
  };

  regenerateQuery = async (data: ChatApi.RegenerateQuery.Request) => {
    const { metadata } = data.context;
    const preparedContext = prepareContextData(data.context);
    const postData = {
      ...data,
      context: {
        ...(preparedContext as object),
        ...(metadata ? { metadata } : {}),
      },
    };
    const res = await this.authenticatedClient.post<ChatApi.RegenerateQuery.Response>(
      `/chat/regenerate-query`,
      postData
    );
    return {
      identifier: res.data.identifier,
    };
  };

  getChatHistory = async (
    data: ChatApi.GetChatHistory.Request
  ): Promise<ChatApi.GetChatHistory.Response<Date>> => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify(
      {
        cursor: data.cursor,
        pageSize: data.pageSize,
        context,
        mode: data.mode,
        includeAdditional: data.includeAdditional,
        brandIdentifier: data.brandIdentifier,
        isBookmaked: data.isBookmarked,
        name: data.name,
        applicationIdentifier: data.applicationIdentifier,
      },
      {
        arrayFormat: 'bracket',
        skipNull: true,
      }
    );

    const res = await this.authenticatedClient.get<ChatApi.GetChatHistory.Response>(`/chat/history?${qs}`);

    return {
      hasMore: res.data.hasMore,
      items: res.data.items.map(transformChatInstance),
      shared: res.data.shared.map(transformChatInstance),
    };
  };

  searchChatHistory = async (
    data: ChatApi.SearchChatHistory.Request
  ): Promise<ChatApi.SearchChatHistory.Response<Date>> => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify(
      {
        cursor: data.cursor,
        pageSize: data.pageSize,
        context,
        mode: data.mode,
        includeAdditional: data.includeAdditional,
        brandIdentifier: data.brandIdentifier,
        name: data.name,
        isBookmarked: data.isBookmarked,
        applicationIdentifier: data.applicationIdentifier,
      },
      {
        arrayFormat: 'bracket',
        skipNull: true,
      }
    );

    const res = await this.authenticatedClient.get<ChatApi.SearchChatHistory.Response>(
      `/chat/history/search?${qs}`
    );

    return {
      hasMore: res.data.hasMore,
      items: res.data.items.map(transformChatInstance),
      shared: res.data.shared.map(transformChatInstance),
    };
  };

  getChatInitialMessage = async (data: ChatApi.GetChatInitialMessage.Request) => {
    const context = stringifyContextData(data.context);

    const qs = queryString.stringify(
      {
        context,
      },
      {
        skipNull: true,
      }
    );

    const res = await this.authenticatedClient.get<ChatApi.GetChatInitialMessage.Response>(
      `/chat/initial-message?${qs}`
    );
    return res.data;
  };

  lookupContext = async (data: ChatApi.LookupContext.Request) => {
    const qs = queryString.stringify({
      contextType: data.contextType,
      entityId: data.entityId,
      objectId: data.objectId,
    });

    const res = await this.authenticatedClient.get<ChatApi.LookupContext.Response>(`/chat/context?${qs}`);
    return res.data;
  };

  getChatContextAccess = async (data: ChatApi.GetChatContextAccess.Request) => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify(
      {
        context,
      },
      {
        skipNull: true,
      }
    );

    const res = await this.authenticatedClient.get<ChatApi.GetChatContextAccess.Response>(
      `/chat/context-access?${qs}`
    );
    return res.data;
  };

  searchChatFilters = async (data: ChatApi.SearchChatFilters.Request) => {
    const res = await this.authenticatedClient.get<ChatApi.SearchChatFilters.Response>(
      `/chat/filters/search?${queryString.stringify(data, { arrayFormat: 'bracket' })}`
    );
    return res.data;
  };

  searchChatProjects = async (data: ChatApi.SearchChatProjects.Request) => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify(
      {
        context,
        query: data.query,
      },
      {
        skipNull: true,
      }
    );

    const res = await this.authenticatedClient.get<ChatApi.SearchChatProjects.Response>(
      `/chat/filters/projects?${qs}`
    );
    return res.data;
  };

  searchChatRecords = async (data: ChatApi.SearchChatRecords.Request) => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify(
      {
        context,
        query: data.query,
        projectIds: data.projectIds,
        sourceTypes: data.sourceTypes,
      },
      {
        skipNull: true,
        arrayFormat: 'bracket',
      }
    );

    const res = await this.authenticatedClient.get<ChatApi.SearchChatRecords.Response>(
      `/chat/filters/records?${qs}`
    );
    return res.data;
  };

  getPendingQuery = async (data: ChatApi.GetPendingQuery.Request) => {
    return this.authenticatedClient
      .get<ChatApi.GetPendingQuery.Response>(`/chat/pending-query?queryIdentifier=${data.queryIdentifier}`)
      .then((res) => ({
        chatInstance: transformChatInstance(res.data.chatInstance),
        query: transformChatQuery(res.data.query),
      }));
  };

  contextSearch = ({ type, q, contextIdentifier }: ChatApi.ContextSearch.Request) => {
    return this.authenticatedClient.get<ChatApi.ContextSearch.Response>(
      `/chat/context-search?${queryString.stringify({ type, q, contextIdentifier })}`
    );
  };

  searchTags = async (data: ChatApi.SearchTags.Request) => {
    const context = stringifyContextData(prepareContextData(data.context));

    const qs = queryString.stringify(
      {
        context,
        query: data.query,
      },
      {
        skipNull: true,
      }
    );

    return this.authenticatedClient.get<ChatApi.SearchTags.Response>(`/tags?${qs}`);
  };

  submitChatAction = async (data: ChatApi.SubmitChatAction.Request) => {
    return this.authenticatedClient.post<ChatApi.SubmitChatAction.Response>('/chat/actions', data);
  };

  saveFormWorkflowSubmission = async (data: ChatApi.SaveFormWorkflowSubmission.Request) => {
    const res = await this.authenticatedClient.post<ChatApi.SaveFormWorkflowSubmission.Response>(
      '/chat/form-workflow/submit',
      data
    );
    return res.data;
  };

  sendHcpSchedulingEmail = async (data: ChatApi.SendHcpSchedulingEmail.Request) => {
    return this.authenticatedClient
      .post<ChatApi.SendHcpSchedulingEmail.Response>('/chat/send-hcp-scheduling-email', data)
      .then((res) => ({
        query: transformChatQuery(res.data.query),
      }));
  };

  getChatContexts = async (): Promise<ChatApi.GetChatContexts.Response> => {
    const res = await this.authenticatedClient.get<ChatApi.GetChatContexts.Response>('/chat/contexts');
    return res.data;
  };

  getChatQuestions = async (): Promise<ChatApi.GetChatQuestions.Response> => {
    const res = await this.authenticatedClient.get<ChatApi.GetChatQuestions.Response>('/chat/questions');
    return res.data;
  };

  getChatLandingCards = async (): Promise<ChatApi.GetChatLandingCards.Response> => {
    const res = await this.authenticatedClient.get<ChatApi.GetChatLandingCards.Response>(
      '/chat/landing-cards'
    );
    return res.data;
  };

  fetchLoadingAudioClip = async (): Promise<string> => {
    const response = await this.authenticatedClient.get('/chat/generate-loading-audio-clip');
    if (response.data.audioUrl) {
      return response.data.audioUrl;
    }
    throw new Error('Invalid audio data received');
  };
}
